import { useCallback } from 'react';
import { matchPath } from 'react-router';
import { useClientTokenManager, useFeatureFlags } from '../../context';
import { APP_CONFIG } from '../../environment/app';
export const isFeatureFlagged = (environments) => {
    if (!environments) {
        return true;
    }
    if (!APP_CONFIG.environment) {
        return true;
    }
    return environments.includes(APP_CONFIG.environment);
};
export const useRoute = () => {
    const { tokenProperties } = useClientTokenManager();
    const { getFeatureFlag } = useFeatureFlags();
    const { permissions: userPermissions } = tokenProperties;
    const canAccessRoute = useCallback((routeConfig) => {
        const { permissions, on, featureFlagName, routes: subRoutes } = routeConfig;
        if (!userPermissions.length) {
            return false;
        }
        // LaunchDarkly Feature flagging
        if (featureFlagName && !getFeatureFlag(featureFlagName)) {
            return false;
        }
        // Feature flagging
        if (!isFeatureFlagged(on)) {
            return false;
        }
        if (subRoutes) {
            let mainRouteAccess = true;
            if (permissions) {
                if (typeof permissions === 'function') {
                    mainRouteAccess = permissions(userPermissions);
                }
                else {
                    mainRouteAccess = permissions.some((permission) => userPermissions.includes(permission));
                }
            }
            // Tests user has permission to access at least one sub-route + main route
            return subRoutes.some((subRoute) => canAccessRoute(subRoute)) && mainRouteAccess;
        }
        // No permissions have been set
        if (!permissions) {
            return true;
        }
        if (typeof permissions === 'function') {
            return permissions(userPermissions);
        }
        // Tests all permissions
        return permissions.every((permission) => userPermissions.includes(permission));
    }, [userPermissions, getFeatureFlag]);
    const canAccessRouteByPath = useCallback((path, routeConfigs) => {
        const routeMatch = routeConfigs?.find((route) => {
            return matchPath(path, route);
        });
        if (!routeMatch) {
            return false;
        }
        // Perfect match === corresponding route
        if (routeMatch.path === path) {
            return canAccessRoute(routeMatch);
        }
        // Tests sub-routes
        if (routeMatch.routes) {
            return canAccessRouteByPath(path, routeMatch.routes);
        }
        return false;
    }, [canAccessRoute]);
    const getRequiredAccountLevels = (path, routeConfigs) => {
        const routeMatch = routeConfigs?.find((route) => {
            return matchPath(path, route);
        });
        return routeMatch?.requiredAccountLevels;
    };
    return { canAccessRoute, canAccessRouteByPath, getRequiredAccountLevels };
};
